import React from 'react';
import { SvgIcon, SvgIconProps } from '@mui/material';

export interface BankLogoConfig {
    /**
     * Colors for the logo
     */
    colors: {
        background: string;
        border?: string;
        logo: string;
    };
    /**
     * Default bank name for accessibility
     */
    defaultAlt: string;
}

export interface BaseBankLogoProps extends Omit<SvgIconProps, 'viewBox'> {
    /**
     * The size of the logo
     * @default 'md'
     */
    size?: 'xs' | 'sm' | 'md' | 'lg';
    /**
     * Shape of the logo background
     * @default 'circle'
     */
    shape?: 'circle' | 'square';
    /**
     * Alt text for accessibility
     */
    alt?: string;
    /**
     * Logo configuration (colors, default alt text)
     */
    config: BankLogoConfig;
    /**
     * Render function for the logo content
     */
    renderLogo: (colors: { background: string; border?: string; logo: string }, clipPathId: string) => React.ReactNode;
}

/**
 * Base component for bank logos with flexible sizing and shapes
 */
function BaseBankLogo({
    size = 'md',
    shape = 'circle',
    alt = '',
    config,
    renderLogo,
    sx,
    ...props
}: BaseBankLogoProps) {
    // Calculate dimensions based on size
    const dimensions = React.useMemo(() => {
        switch (size) {
            case 'xs':
                return { width: 24, height: 24 };
            case 'sm':
                return { width: 32, height: 32 };
            case 'lg':
                return { width: 64, height: 64 };
            case 'md':
            default:
                return { width: 48, height: 48 };
        }
    }, [size]);

    // Get colors from config
    const colors = React.useMemo(() => {
        return config.colors;
    }, [config.colors]);

    // Generate unique IDs to avoid conflicts when multiple logos are on the same page
    const clipPathId = React.useMemo(() => `bank-logo-clip-${Math.random().toString(36).substring(2, 11)}`, []);

    // Use provided alt text or fall back to config default
    const altText = alt || config.defaultAlt;

    // .MuiSvgIcon-root fill is always "currentColor". Set it to background color
    return (
        <SvgIcon
            {...props}
            sx={{
                width: dimensions.width,
                height: dimensions.height,
                '&.MuiSvgIcon-root': {
                    fill: 'none', // Remove default fill to let individual elements control their own fill
                },
                ...sx,
            }}
            viewBox="0 0 64 64"
            role="img"
            aria-label={altText}>
            <title>{altText}</title>

            {/* Background shape */}
            {shape === 'circle' ? (
                <>
                    <circle cx="32" cy="32" r="31.5" fill={colors.background} />
                    {colors.border && <circle cx="32" cy="32" r="31.5" stroke={colors.border} strokeWidth="1" />}
                </>
            ) : (
                <>
                    <rect x="0.5" y="0.5" width="63" height="63" rx="8" fill={colors.background} />
                    {colors.border && (
                        <rect x="0.5" y="0.5" width="63" height="63" rx="8" stroke={colors.border} strokeWidth="1" />
                    )}
                </>
            )}

            {/* Logo content */}
            {renderLogo(colors, clipPathId)}
        </SvgIcon>
    );
}

export default BaseBankLogo;
